import React from 'react';
import { Edit3, Calendar, Users, Zap, Lightbulb, Palette, BarChart3, Image } from 'lucide-react';

/**
 * GetStartedWorkflow Component
 * 
 * Shows a guided workflow for users who have an empty collection (no event, no artworks).
 * Displays as a dark header banner with 4 main steps in a horizontal layout.
 * 
 * Props:
 * - onEditCollection: function (step 1 action)
 * - onAddExhibition: function (step 2 action)
 * - onCreateSubmission: function (step 3 action)
 * - onAcceptArtworks: function (step 4 action)
 * - onAddArtwork: function (add artwork action)
 */
const GetStartedWorkflow = ({
  onEditCollection,
  onAddExhibition,
  onCreateSubmission,
  onAcceptArtworks,
  onAddArtwork
}) => {
  const steps = [
    {
      number: 1,
      title: 'Edit Collection',
      description: 'Add title, description, and cover image',
      action: 'Edit Collection',
      onClick: onEditCollection,
      icon: Edit3
    },
    {
      number: 2,
      title: 'Create Exhibition',
      description: 'Link an event with dates and venue',
      action: 'Add Exhibition',
      onClick: onAddExhibition,
      icon: Calendar
    },
    {
      number: 3,
      title: 'Receive Submissions',
      description: 'Artists submit artworks to your collection',
      action: 'Create Submission',
      onClick: onCreateSubmission,
      icon: Users
    },
    {
      number: 4,
      title: 'Curate Exhibition',
      description: 'Accept artworks and plan your gallery',
      action: 'Accept Artworks',
      onClick: onAcceptArtworks,
      icon: Zap
    }
  ];

  return (
    <div className="get-started-workflow">
      {/* Header Banner */}
      <div className="workflow-banner">
        <div className="banner-header">
          <h2 className="banner-title">Get Started with Your Collection</h2>
          <p className="banner-subtitle">Follow these 4 steps to set up your collection and start curating</p>
        </div>

        {/* Steps in Horizontal Layout */}
        <div className="workflow-steps-horizontal">
          {steps.map((step) => {
            const IconComponent = step.icon;
            return (
              <div key={step.number} className="step-card">
                {/* Step Number and Icon */}
                <div className="step-badge-horizontal">
                  <div className="step-number-badge">{step.number}</div>
                  <IconComponent size={20} className="step-icon-badge" />
                </div>

                {/* Step Content */}
                <div className="step-info">
                  <h3 className="step-name">{step.title}</h3>
                  <p className="step-info-text">{step.description}</p>
                </div>

                {/* Step Action Button */}
                <button
                  className="step-btn"
                  onClick={step.onClick}
                  title={step.action}
                >
                  {step.action}
                </button>
              </div>
            );
          })}
        </div>
        
        {/* Quick Add Artwork Option */}
        {onAddArtwork && (
          <div className="quick-add-section">
            <div className="divider-text">
              <span>or add artwork directly</span>
            </div>
            <button className="add-artwork-btn" onClick={onAddArtwork}>
              <Image size={20} />
              Browse & Add Artwork
            </button>
          </div>
        )}
      </div>

      {/* Tips Section */}
      <div className="workflow-tips">
        <div className="tips-container">
          <div className="tip-item">
            <div className="tip-icon-wrapper">
              <Lightbulb size={20} className="tip-icon-lucide" />
            </div>
            <div className="tip-content">
              <h4>Tip: Exhibition Link</h4>
              <p>If you're using Eventbrite, we'll sync your exhibition details automatically</p>
            </div>
          </div>

          <div className="tip-item">
            <div className="tip-icon-wrapper">
              <Palette size={20} className="tip-icon-lucide" />
            </div>
            <div className="tip-content">
              <h4>Curator Tools</h4>
              <p>Once you have artworks, unlock Display Cards, Wall Planner, and 3D Gallery tools</p>
            </div>
          </div>

          <div className="tip-item">
            <div className="tip-icon-wrapper">
              <BarChart3 size={20} className="tip-icon-lucide" />
            </div>
            <div className="tip-content">
              <h4>Track Everything</h4>
              <p>Monitor submissions, check-ins, voting, and artist inquiries from one dashboard</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GetStartedWorkflow;
